import React, { useEffect, useState, useContext } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Button from "../../components/common/Button";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUrl";
import { AuthContext } from "../../context/AuthContext";

export default function ConfirmDealPage() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  async function load() {
    setLoading(true);
    try {
      const res = await api.get(`/deals/${id}`);
      setDeal(res.data);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Failed to load deal");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [id]);

  async function confirm() {
    try {
      const res = await api.post(`/deals/${id}/confirm`);
      setDeal(res.data);

      if (user.role === "OWNER") {
        toast.success("You have confirmed the deal.");
        nav("/owner");
      } else {
        toast.success("You have confirmed the deal.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to confirm deal");
    }
  }

  async function pay() {
    try {
      const res = await api.post(`/deals/${id}/pay`);
      setDeal(res.data);
      toast.success("Service charge paid. Waiting for admin verification.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Payment failed");
    }
  }

  async function adminVerify() {
    try {
      const res = await api.post(`/deals/${id}/admin-verify`);
      setDeal(res.data);
      toast.success("Deal verified. Room is now hidden from public.");
      nav("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to verify deal");
    }
  }

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-10">Loading deal...</div>;
  }

  if (!deal) {
    return (
      <div className="max-w-6xl mx-auto px-6 py-10">
        Deal not found.
      </div>
    );
  }

  const { listing, owner, seeker } = deal;

  const isSeeker = user.role === "SEEKER";
  const isOwner = user.role === "OWNER";
  const isAdmin = user.role === "ADMIN";

  const canSeekerConfirm =
    isSeeker && !deal.seekerConfirmed && deal.status === "PENDING";

  const canOwnerConfirm =
    isOwner &&
    deal.seekerConfirmed &&
    !deal.ownerConfirmed &&
    deal.status === "SEEKER_CONFIRMED";

  const canPay =
    isSeeker &&
    deal.seekerConfirmed &&
    deal.ownerConfirmed &&
    !deal.paid &&
    deal.status === "OWNER_CONFIRMED";

  const canAdminVerify =
    isAdmin &&
    deal.seekerConfirmed &&
    deal.ownerConfirmed &&
    deal.paid &&
    !deal.adminVerified &&
    deal.status === "AWAITING_ADMIN_VERIFY";

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-3 gap-8">
      <div className="col-span-2">
        {/* Room details */}
        <div className="bg-white p-6 rounded-2xl shadow mb-6">
          <h2 className="font-semibold text-xl">Room Details</h2>
          <div className="flex items-center gap-4 mt-4">
            <div className="w-24 h-20 bg-gray-100 rounded-xl overflow-hidden">
              <img
                src={getImageUrl(
                  listing.images?.[0] || "/upload/rooms/sample.jpg"
                )}
                alt="room"
                className="rounded-2xl w-full h-20 object-cover"
              />
            </div>
            <div>
              <div className="font-semibold">{listing.title}</div>
              <div className="text-gray-500">{listing.location}</div>
            </div>
            <div className="ml-auto text-primary font-bold text-xl">
              Rs. {deal.rentAmount}
            </div>
          </div>
        </div>

        {/* Deal summary + actions */}
        <div className="bg-white p-6 rounded-2xl shadow">
          <h2 className="font-semibold text-xl mb-4">Deal Summary</h2>
          <div className="flex justify-between mb-2">
            <span>Monthly Rent</span>
            <span>Rs. {deal.rentAmount}</span>
          </div>
          <div className="flex justify-between mb-2">
            <span>Service Charge</span>
            <span>Rs. {deal.serviceCharge}</span>
          </div>
          <div className="flex justify-between border-t pt-3 mt-3 font-semibold">
            <span>Total First Payment</span>
            <span>Rs. {deal.rentAmount + deal.serviceCharge}</span>
          </div>

          <div className="mt-6 flex gap-3 flex-wrap">
            {canSeekerConfirm && (
              <Button variant="primary" onClick={confirm}>
                Confirm Deal (Seeker)
              </Button>
            )}

            {canOwnerConfirm && (
              <Button variant="primary" onClick={confirm}>
                Confirm Deal (Owner)
              </Button>
            )}

            {canPay && (
              <Button variant="outline" onClick={pay}>
                Pay Service Charge
              </Button>
            )}

            {canAdminVerify && (
              <Button variant="primary" onClick={adminVerify}>
                Verify Deal (Admin)
              </Button>
            )}
          </div>

          <p className="text-xs text-gray-400 mt-3">
            Flow: Seeker confirms → Owner confirms → Seeker pays service charge
            → Admin verifies → Deal completed & room hidden.
          </p>
        </div>
      </div>

      <aside>
        {/* Seeker (and Admin) see OWNER info */}
        {(isSeeker || isAdmin) && (
          <div className="bg-white p-6 rounded-2xl shadow mb-4">
            <h3 className="font-semibold mb-2">Owner Details</h3>
            <p>{owner?.fullName || "N/A"}</p>
            <p className="text-xs text-gray-500">{owner?.email || "N/A"}</p>
          </div>
        )}

        {/* Owner (and Admin) see SEEKER info */}
        {(isOwner || isAdmin) && (
          <div className="bg-white p-6 rounded-2xl shadow mb-4">
            <h3 className="font-semibold mb-2">Seeker Details</h3>
            {seeker ? (
              <>
                <p>{seeker.fullName}</p>
                <p className="text-xs text-gray-500">{seeker.email}</p>
              </>
            ) : (
              <p className="text-xs text-gray-500">
                No seeker information available.
              </p>
            )}
          </div>
        )}

        {/* Status (all roles) */}
        <div className="bg-white p-6 rounded-2xl shadow">
          <h3 className="font-semibold mb-2">Status</h3>
          <p className="text-sm">Current status: {deal.status}</p>
          <p className="text-sm">
            Seeker confirmed: {deal.seekerConfirmed ? "Yes" : "No"}
          </p>
          <p className="text-sm">
            Owner confirmed: {deal.ownerConfirmed ? "Yes" : "No"}
          </p>
          <p className="text-sm">Paid: {deal.paid ? "Yes" : "No"}</p>
          <p className="text-sm">
            Admin verified: {deal.adminVerified ? "Yes" : "No"}
          </p>
        </div>
      </aside>
    </div>
  );
}
